import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../services/api';
import { useAuth } from '../AuthContext';
import CycleCountMetrics from './CycleCountMetrics';
import {
    ClipboardList, RefreshCcw, MapPin, Box, UserCheck,
    CheckCircle2, AlertCircle, Save, ArrowUpDown, Plus, X,
    ChevronUp, ChevronDown, Check, CheckCheck, Trash2,
    ChevronRight, Calendar, Search, Filter, History
} from 'lucide-react';

const STATUS_CONFIG = {
    pending: { color: '#64748b', bg: '#f1f5f9', label: 'Pending' },
    in_progress: { color: '#4f46e5', bg: '#eef2ff', label: 'In Progress' },
    completed: { color: '#10b981', bg: '#ecfdf5', label: 'Completed' },
    cancelled: { color: '#ef4444', bg: '#fef2f2', label: 'Cancelled' }
};

const StatusBadge = ({ status }) => {
    const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.pending;
    return (
        <span style={{
            padding: '0.25rem 0.6rem', borderRadius: '9999px', fontSize: '0.7rem',
            fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.025em',
            backgroundColor: cfg.bg, color: cfg.color
        }}>
            {cfg.label}
        </span>
    );
};

const useSortableData = (items, config = null) => {
    const [sortConfig, setSortConfig] = useState(config);
    const sortedItems = useMemo(() => {
        let sortableItems = [...items];
        if (sortConfig !== null) {
            sortableItems.sort((a, b) => {
                if (a[sortConfig.key] < b[sortConfig.key]) return sortConfig.direction === 'ascending' ? -1 : 1;
                if (a[sortConfig.key] > b[sortConfig.key]) return sortConfig.direction === 'ascending' ? 1 : -1;
                return 0;
            });
        }
        return sortableItems;
    }, [items, sortConfig]);
    const requestSort = (key) => {
        let direction = 'ascending';
        if (sortConfig && sortConfig.key === key && sortConfig.direction === 'ascending') direction = 'descending';
        setSortConfig({ key, direction });
    };
    return { items: sortedItems, requestSort, sortConfig };
};

// ═══════════════════════════════════════════════════════════════════════════
// ADMIN VIEW
// ═══════════════════════════════════════════════════════════════════════════
const AdminCycleCount = () => {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [expandedRow, setExpandedRow] = useState(null);
    const [binsByRow, setBinsByRow] = useState({});
    const [activeTab, setActiveTab] = useState('overview');

    // Filter states
    const [monthFilter, setMonthFilter] = useState(new Date().toISOString().slice(0, 7)); // YYYY-MM
    const [warehouseFilter, setWarehouseFilter] = useState('All');
    const [statusFilter, setStatusFilter] = useState('All');
    const [searchQuery, setSearchQuery] = useState('');

    // Assignment Form states
    const [showAssignForm, setShowAssignForm] = useState(false);
    const [options, setOptions] = useState({ warehouses: [], skus: [], clients: [], pickers: [] });
    const [newAssignment, setNewAssignment] = useState({ client: '', sku: '', warehouse: '', assigned_to: '' });
    const [skusByClient, setSkusByClient] = useState([]);
    const [selectedSkus, setSelectedSkus] = useState([]); // Array of "sku|warehouse"
    const [skuSearchQuery, setSkuSearchQuery] = useState('');
    const [creating, setCreating] = useState(false);

    const fetchAssignments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.inventory.getAssignments();
            if (res.data) setAssignments(res.data);
        } catch (e) {
            console.error('Failed to fetch assignments', e);
        } finally {
            setLoading(false);
        }
    }, []);
    const fetchOptions = useCallback(async () => {
        try {
            const res = await api.inventory.getCycleCountOptions();
            if (res.data) setOptions(res.data);
        } catch (e) {
            console.error('Failed to fetch options', e);
        }
    }, []);

    useEffect(() => {
        fetchAssignments();
        fetchOptions();
    }, [fetchAssignments, fetchOptions]);

    const handleClientChange = async (client) => {
        setNewAssignment(prev => ({ ...prev, client, sku: '', warehouse: '' }));
        setSelectedSkus([]);
        setSkuSearchQuery('');
        if (client && client !== 'All') {
            try {
                const res = await api.inventory.getSKUsByClient(client);
                setSkusByClient(res.data || []);
            } catch (e) {
                console.error('Failed to fetch SKUs by client', e);
            }
        } else {
            setSkusByClient([]);
        }
    };

    const toggleSku = (sku, warehouse) => {
        const key = `${sku}|${warehouse}`;
        setSelectedSkus(prev =>
            prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
        );
    };

    const handleCreateAssignment = async () => {
        const { assigned_to, client } = newAssignment;
        if (selectedSkus.length === 0 || !assigned_to) {
            alert('Please select at least one SKU and a Picker.');
            return;
        }
        setCreating(true);
        try {
            await Promise.all(selectedSkus.map(async (key) => {
                const [sku, warehouse] = key.split('|');
                return api.inventory.createAssignment({
                    sku,
                    warehouse,
                    assigned_to,
                    clientName: client
                });
            }));
            setShowAssignForm(false);
            setNewAssignment({ client: '', sku: '', warehouse: '', assigned_to: '' });
            setSelectedSkus([]);
            fetchAssignments();
        } catch (e) {
            alert(e.response?.data?.error || 'Failed to create some assignments');
            fetchAssignments(); // Refresh anyway to show what was created
        } finally {
            setCreating(false);
        }
    };

    const handleDeleteAssignment = async (id) => {
        if (!window.confirm('Delete this assignment?')) return;
        try {
            await api.inventory.deleteAssignment(id);
            fetchAssignments();
        } catch (e) {
            alert('Failed to delete.');
        }
    };

    const handleExpandRow = async (a) => {
        if (expandedRow === a.id) {
            setExpandedRow(null);
            return;
        }
        setExpandedRow(a.id);
        // Only fetch if not already loaded or loading
        if (binsByRow[a.id] === undefined) {
            setBinsByRow(prev => ({ ...prev, [a.id]: 'loading' }));
            try {
                const res = await api.inventory.getCycleCountBins(a.sku, a.warehouse);
                setBinsByRow(prev => ({ ...prev, [a.id]: res.data || [] }));
            } catch (e) {
                console.error('Failed to fetch bins', e);
                setBinsByRow(prev => ({ ...prev, [a.id]: [] }));
            }
        }
    };

    const filteredAssignments = assignments.filter(a => {
        const matchesMonth = monthFilter === 'All' || a.created_at.startsWith(monthFilter);
        const matchesWarehouse = warehouseFilter === 'All' || a.warehouse === warehouseFilter;
        const matchesStatus = statusFilter === 'All' || a.status === statusFilter;
        const matchesSearch = !searchQuery ||
            a.sku.toLowerCase().startsWith(searchQuery.toLowerCase()) ||
            (a.client_name && a.client_name.toLowerCase().startsWith(searchQuery.toLowerCase())) ||
            a.assigned_to_name.toLowerCase().startsWith(searchQuery.toLowerCase());
        return matchesMonth && matchesWarehouse && matchesStatus && matchesSearch;
    });

    const warehouses = [...new Set(assignments.map(a => a.warehouse))];

    return (
        <div style={{ padding: '2rem', maxWidth: '1600px', margin: '0 auto' }}>
            <div style={{ marginBottom: '2.5rem', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: '1.5rem' }}>
                <div>
                    <h1 style={{ margin: 0, fontSize: '2rem', fontWeight: 800, color: 'var(--text-main)', letterSpacing: '-0.02em' }}>Cycle Count Management</h1>
                    <p style={{ margin: '0.4rem 0 0', color: 'var(--text-muted)', fontSize: '1rem' }}>Monitor and review inventory verification tasks</p>
                </div>
                {activeTab === 'assignments' && (
                    <div style={{ display: 'flex', gap: '0.75rem' }}>
                        <button
                            onClick={() => setShowAssignForm(!showAssignForm)}
                            style={{
                                padding: '0.6rem 1.2rem',
                                borderRadius: '10px',
                                border: 'none',
                                background: showAssignForm ? '#64748b' : 'var(--primary)',
                                color: 'white',
                                cursor: 'pointer',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '0.5rem',
                                fontWeight: 600,
                                fontSize: '0.9rem',
                                boxShadow: '0 4px 12px rgba(79, 70, 229, 0.2)'
                            }}
                        >
                            {showAssignForm ? <X size={16} /> : <Plus size={16} />}
                            {showAssignForm ? 'Cancel' : 'Assign New Task'}
                        </button>
                        <button onClick={fetchAssignments} style={{ padding: '0.6rem 1.2rem', borderRadius: '10px', border: '1px solid #e2e8f0', background: 'white', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '0.5rem', fontWeight: 600, fontSize: '0.9rem', color: 'var(--text-main)' }}>
                            <RefreshCcw size={16} /> Refresh
                        </button>
                    </div>
                )}
            </div>

            {/* Tab Navigation */}
            <div className="tabs-container" style={{ marginBottom: '1.5rem', display: 'flex', gap: '0.5rem', borderBottom: '1px solid #e2e8f0', paddingBottom: '2px' }}>
                <button
                    className={`tab-btn ${activeTab === 'overview' ? 'active' : ''}`}
                    onClick={() => setActiveTab('overview')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        background: 'transparent',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: activeTab === 'overview' ? 'var(--primary)' : 'var(--text-muted)',
                        borderBottom: activeTab === 'overview' ? '3px solid var(--primary)' : '3px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    Monthly Overview
                </button>
                <button
                    className={`tab-btn ${activeTab === 'assignments' ? 'active' : ''}`}
                    onClick={() => setActiveTab('assignments')}
                    style={{
                        padding: '0.75rem 1.5rem',
                        border: 'none',
                        background: 'transparent',
                        fontWeight: 600,
                        fontSize: '0.95rem',
                        color: activeTab === 'assignments' ? 'var(--primary)' : 'var(--text-muted)',
                        borderBottom: activeTab === 'assignments' ? '3px solid var(--primary)' : '3px solid transparent',
                        cursor: 'pointer',
                        transition: 'all 0.2s'
                    }}
                >
                    Assignments
                </button>
            </div>

            {activeTab === 'overview' && <CycleCountMetrics />}

            {activeTab === 'assignments' && (
                <>
                    {showAssignForm && (
                        <div className="card" style={{ marginBottom: '1.5rem', padding: '1.5rem', background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)', border: '1px solid var(--primary)', boxShadow: '0 10px 25px -5px rgba(79, 70, 229, 0.1)' }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <h3 style={{ margin: 0, display: 'flex', alignItems: 'center', gap: '0.6rem', color: 'var(--primary)' }}>
                                    <Plus size={20} /> Assign New Verification Task
                                </h3>
                                <button onClick={() => setShowAssignForm(false)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#94a3b8' }}><X size={20} /></button>
                            </div>
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '1.25rem', marginBottom: '1.5rem' }}>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.85rem' }}>Client</label>
                                    <select value={newAssignment.client} onChange={e => handleClientChange(e.target.value)} style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <option value="">Select Client</option>
                                        {options.clients.map(c => <option key={c} value={c}>{c}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.85rem' }}>Warehouse</label>
                                    <select value={newAssignment.warehouse} onChange={e => {
                                        const wh = e.target.value;
                                        setNewAssignment(p => {
                                            const currentPicker = options.pickers.find(u => String(u.id) === String(p.assigned_to));
                                            const shouldResetPicker = wh && currentPicker && currentPicker.warehouse !== wh;
                                            return { ...p, warehouse: wh, assigned_to: shouldResetPicker ? '' : p.assigned_to };
                                        });
                                    }} style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <option value="">All Warehouses</option>
                                        {options.warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600, fontSize: '0.85rem' }}>Assign To</label>
                                    <select value={newAssignment.assigned_to} onChange={e => setNewAssignment(p => ({ ...p, assigned_to: e.target.value }))} style={{ width: '100%', padding: '0.6rem', borderRadius: '8px', border: '1px solid #e2e8f0' }}>
                                        <option value="">Select Picker</option>
                                        {options.pickers
                                            .filter(u => !newAssignment.warehouse || u.warehouse === newAssignment.warehouse)
                                            .map(u => <option key={u.id} value={u.id}>{u.username} ({u.warehouse || 'No WH'})</option>)}
                                    </select>
                                </div>
                            </div>

                            {newAssignment.client && (
                                <div style={{ marginBottom: '1.5rem' }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
                                        <label style={{ fontWeight: 600, fontSize: '0.85rem' }}>Select SKUs ({selectedSkus.length} selected)</label>
                                        <div style={{ position: 'relative', width: '250px' }}>
                                            <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: '#94a3b8' }} />
                                            <input
                                                type="text"
                                                placeholder="Search SKU or Product..."
                                                value={skuSearchQuery}
                                                onChange={e => setSkuSearchQuery(e.target.value)}
                                                style={{ width: '100%', padding: '0.4rem 0.4rem 0.4rem 2rem', borderRadius: '6px', border: '1px solid #e2e8f0', fontSize: '0.8rem' }}
                                            />
                                        </div>
                                    </div>
                                    <div style={{ maxHeight: '300px', overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: '8px', background: 'white' }}>
                                        <table className="responsive-table" style={{ width: '100%', fontSize: '0.8rem' }}>
                                            <thead style={{ position: 'sticky', top: 0, background: '#f8fafc', zIndex: 1 }}>
                                                <tr>
                                                    <th style={{ width: '40px' }}>
                                                        <input
                                                            type="checkbox"
                                                            onChange={e => {
                                                                const filtered = skusByClient.filter(s => {
                                                                    const matchesSearch = s.sku.toLowerCase().startsWith(skuSearchQuery.toLowerCase()) || s.product_name.toLowerCase().startsWith(skuSearchQuery.toLowerCase());
                                                                    const matchesWarehouse = !newAssignment.warehouse || s.warehouse === newAssignment.warehouse;
                                                                    return matchesSearch && matchesWarehouse;
                                                                });
                                                                if (e.target.checked) {
                                                                    const newKeys = filtered.map(s => `${s.sku}|${s.warehouse}`);
                                                                    setSelectedSkus(prev => [...new Set([...prev, ...newKeys])]);
                                                                } else {
                                                                    const keysToRemove = filtered.map(s => `${s.sku}|${s.warehouse}`);
                                                                    setSelectedSkus(prev => prev.filter(k => !keysToRemove.includes(k)));
                                                                }
                                                            }}
                                                        />
                                                    </th>
                                                    <th>SKU</th>
                                                    <th>Product Name</th>
                                                    <th>Warehouse</th>
                                                    <th>On Hand</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {skusByClient
                                                    .filter(s => {
                                                        const matchesSearch = s.sku.toLowerCase().startsWith(skuSearchQuery.toLowerCase()) || s.product_name.toLowerCase().startsWith(skuSearchQuery.toLowerCase());
                                                        const matchesWarehouse = !newAssignment.warehouse || s.warehouse === newAssignment.warehouse;
                                                        return matchesSearch && matchesWarehouse;
                                                    })
                                                    .map(s => (
                                                        <tr key={`${s.sku}|${s.warehouse}`} onClick={() => toggleSku(s.sku, s.warehouse)} style={{ cursor: 'pointer' }}>
                                                            <td>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedSkus.includes(`${s.sku}|${s.warehouse}`)}
                                                                    onChange={() => { }} // Handled by tr onClick
                                                                />
                                                            </td>
                                                            <td style={{ fontWeight: 600, color: 'var(--primary)' }}>{s.sku}</td>
                                                            <td>{s.product_name}</td>
                                                            <td>{s.warehouse}</td>
                                                            <td>{s.total_on_hand}</td>
                                                        </tr>
                                                    ))}
                                            </tbody>
                                        </table>
                                    </div>
                                </div>
                            )}
                            <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                                <button
                                    onClick={handleCreateAssignment}
                                    disabled={creating || selectedSkus.length === 0 || !newAssignment.assigned_to}
                                    style={{
                                        padding: '0.7rem 2rem',
                                        background: 'var(--primary)',
                                        color: 'white',
                                        border: 'none',
                                        borderRadius: '8px',
                                        fontWeight: 700,
                                        cursor: (creating || selectedSkus.length === 0 || !newAssignment.assigned_to) ? 'not-allowed' : 'pointer',
                                        opacity: (creating || selectedSkus.length === 0 || !newAssignment.assigned_to) ? 0.6 : 1
                                    }}
                                >
                                    {creating ? 'Creating...' : `Create ${selectedSkus.length} Task${selectedSkus.length > 1 ? 's' : ''}`}
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Filters Bar */}
                    <div className="card" style={{ marginBottom: '1.5rem', padding: '1.25rem', display: 'flex', gap: '1.25rem', flexWrap: 'wrap', alignItems: 'center', background: '#f8fafc' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <Calendar size={18} color="var(--text-muted)" />
                            <input type="month" value={monthFilter} onChange={e => setMonthFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem' }} />
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <MapPin size={18} color="var(--text-muted)" />
                            <select value={warehouseFilter} onChange={e => setWarehouseFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', minWidth: '150px' }}>
                                <option value="All">All Warehouses</option>
                                {warehouses.map(w => <option key={w} value={w}>{w}</option>)}
                            </select>
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.6rem' }}>
                            <History size={18} color="var(--text-muted)" />
                            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} style={{ padding: '0.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', minWidth: '130px' }}>
                                <option value="All">All Statuses</option>
                                <option value="pending">Pending</option>
                                <option value="in_progress">In Progress</option>
                                <option value="completed">Completed</option>
                            </select>
                        </div>
                        <div style={{ flex: 1, minWidth: '200px', position: 'relative' }}>
                            <Search size={18} color="var(--text-muted)" style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)' }} />
                            <input type="text" placeholder="Search SKU, client, or picker..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} style={{ width: '100%', padding: '0.55rem 1rem 0.55rem 2.5rem', borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '0.9rem', outline: 'none' }} />
                        </div>
                    </div>

                    {loading ? (
                        <div style={{ padding: '5rem', textAlign: 'center' }}>
                            <RefreshCcw size={32} className="spin" style={{ opacity: 0.3, margin: '0 auto 1.5rem' }} />
                            <p style={{ color: 'var(--text-muted)', fontWeight: 500 }}>Loading assignments...</p>
                        </div>
                    ) : (
                        <div className="card table-container">
                            <table className="responsive-table">
                                <thead>
                                    <tr>
                                        <th style={{ width: '40px' }}></th>
                                        <th>Date</th>
                                        <th>SKU</th>
                                        <th>Client</th>
                                        <th>Warehouse</th>
                                        <th>Assigned To</th>
                                        <th>Status</th>
                                        <th>Accuracy</th>
                                        <th>Actions</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAssignments.length === 0 ? (
                                        <tr><td colSpan="9" style={{ textAlign: 'center', padding: '4rem', color: 'var(--text-muted)' }}>No assignments found matching filters.</td></tr>
                                    ) : filteredAssignments.map(a => {
                                        const isExpanded = expandedRow === a.id;
                                        const rowBinsData = binsByRow[a.id];
                                        const isRowLoading = rowBinsData === 'loading';
                                        const binsForRow = Array.isArray(rowBinsData) ? rowBinsData : [];
                                        return (
                                            <React.Fragment key={a.id}>
                                                <tr style={{ background: isExpanded ? 'rgba(79,70,229,0.03)' : 'transparent' }}>
                                                    <td style={{ textAlign: 'center' }}>
                                                        <button onClick={() => handleExpandRow(a)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', padding: '4px' }}>
                                                            {isExpanded ? <ChevronUp size={20} /> : <ChevronDown size={20} />}
                                                        </button>
                                                    </td>
                                                    <td data-label="Date" style={{ whiteSpace: 'nowrap', fontSize: '0.85rem' }}>{new Date(a.created_at).toLocaleDateString()}</td>
                                                    <td data-label="SKU" style={{ fontWeight: 700, color: 'var(--primary)' }}>{a.sku}</td>
                                                    <td data-label="Client" style={{ fontWeight: 500 }}>{a.client_name || '—'}</td>
                                                    <td data-label="Warehouse" style={{ fontSize: '0.85rem' }}>{a.warehouse}</td>
                                                    <td data-label="Assigned To" style={{ fontWeight: 500 }}>{a.assigned_to_name}</td>
                                                    <td data-label="Status"><StatusBadge status={a.status} /></td>
                                                    <td data-label="Accuracy">
                                                        {a.status === 'completed' ? (
                                                            <div style={{ fontWeight: 700, color: a.accuracy_rate >= 95 ? '#10b981' : a.accuracy_rate >= 80 ? '#f59e0b' : '#ef4444' }}>
                                                                {a.accuracy_rate}%
                                                            </div>
                                                        ) : '—'}
                                                    </td>
                                                    <td data-label="Actions">
                                                        <div style={{ display: 'flex', gap: '0.6rem', alignItems: 'center' }}>
                                                            <button
                                                                onClick={() => handleExpandRow(a)}
                                                                className="btn-secondary"
                                                                style={{ padding: '0.35rem 0.7rem', fontSize: '0.75rem' }}
                                                            >
                                                                {isExpanded ? 'Hide' : 'View'}
                                                            </button>
                                                            <button
                                                                onClick={() => handleDeleteAssignment(a.id)}
                                                                style={{
                                                                    background: 'none',
                                                                    border: 'none',
                                                                    cursor: 'pointer',
                                                                    color: '#ef4444',
                                                                    padding: '4px',
                                                                    display: 'flex',
                                                                    alignItems: 'center',
                                                                    justifyContent: 'center',
                                                                    borderRadius: '4px',
                                                                    transition: 'background 0.2s'
                                                                }}
                                                                title="Delete Assignment"
                                                                onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
                                                                onMouseOut={e => e.currentTarget.style.background = 'none'}
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                                {isExpanded && (
                                                    <tr>
                                                        <td colSpan="9" style={{ padding: 0, background: 'rgba(79,70,229,0.03)', borderBottom: '2px solid rgba(79,70,229,0.12)' }}>
                                                            <div style={{ padding: '0.75rem 1.25rem 0.75rem 2.5rem' }}>
                                                                {isRowLoading ? (
                                                                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', color: 'var(--text-muted)', fontSize: '0.85rem', padding: '1rem 0' }}>
                                                                        <RefreshCcw size={14} className="spin" /> Loading bin locations...
                                                                    </div>
                                                                ) : binsForRow.length === 0 ? (
                                                                    <div style={{ color: 'var(--text-muted)', fontSize: '0.82rem', padding: '1rem 0' }}>
                                                                        No bin locations found for this SKU.
                                                                    </div>
                                                                ) : (
                                                                    <table className="responsive-table" style={{ width: '100%', maxWidth: '800px', borderCollapse: 'collapse', fontSize: '0.84rem' }}>
                                                                        <thead>
                                                                            <tr>
                                                                                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem' }}>Bin / Location</th>
                                                                                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>System Count</th>
                                                                                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>Physical Count</th>
                                                                                <th style={{ textAlign: 'right', padding: '0.5rem 0.75rem' }}>Variance</th>
                                                                                <th style={{ textAlign: 'left', padding: '0.5rem 0.75rem' }}>Remark</th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {binsForRow.map(bin => {
                                                                                const sysCount = (a.bin_snapshots && a.bin_snapshots[bin.location_name] !== undefined) ? a.bin_snapshots[bin.location_name] : bin.quantity;
                                                                                const physCount = bin.quantity;
                                                                                const variance = physCount - sysCount;
                                                                                return (
                                                                                    <tr key={bin.location_name}>
                                                                                        <td style={{ padding: '0.4rem 0.75rem', fontWeight: 600 }}>{bin.location_name}</td>
                                                                                        <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right' }}>{sysCount.toLocaleString()}</td>
                                                                                        <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 700 }}>{a.status === 'completed' ? physCount.toLocaleString() : '—'}</td>
                                                                                        <td style={{ padding: '0.4rem 0.75rem', textAlign: 'right', fontWeight: 700, color: variance === 0 ? '#10b981' : '#ef4444' }}>
                                                                                            {a.status === 'completed' ? (variance > 0 ? `+${variance}` : variance) : '—'}
                                                                                        </td>
                                                                                        <td style={{ padding: '0.4rem 0.75rem', color: 'var(--text-muted)', fontSize: '0.8rem' }}>{a.remarks?.[bin.location_name] || '—'}</td>
                                                                                    </tr>
                                                                                );
                                                                            })}
                                                                        </tbody>
                                                                    </table>
                                                                )}
                                                            </div>
                                                        </td>
                                                    </tr>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </tbody>
                            </table>
                        </div>
                    )}
                </>
            )}
        </div>
    );
};

// ═══════════════════════════════════════════════════════════════════════════
// PICKER VIEW
// ═══════════════════════════════════════════════════════════════════════════
const PickerCycleCount = () => {
    const { user } = useAuth();
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedAssignment, setSelectedAssignment] = useState(null);
    const [bins, setBins] = useState([]);
    const [binsLoading, setBinsLoading] = useState(false);
    const [edits, setEdits] = useState({});
    const [remarkEdits, setRemarkEdits] = useState({});
    const [updating, setUpdating] = useState({});
    const [messages, setMessages] = useState({});
    const [markingDone, setMarkingDone] = useState(false);
    const [binFlags, setBinFlags] = useState({});
    const [isMobile, setIsMobile] = useState(window.innerWidth < 992);

    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 992);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    const { items: sortedBins, requestSort, sortConfig } = useSortableData(bins);
    const getSortIcon = (key) => {
        if (!sortConfig || sortConfig.key !== key)
            return <ArrowUpDown size={13} style={{ display: 'inline', marginLeft: '4px', opacity: 0.3 }} />;
        return sortConfig.direction === 'ascending' ? <ChevronUp size={13} /> : <ChevronDown size={13} />;
    };

    const fetchAssignments = useCallback(async () => {
        setLoading(true);
        try {
            const res = await api.inventory.getAssignments();
            if (res.data) setAssignments(res.data);
        } catch (e) {
            console.error('Failed to fetch assignments', e);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchAssignments(); }, [fetchAssignments]);

    const handleSelectAssignment = async (assignment) => {
        if (selectedAssignment?.id === assignment.id) {
            setSelectedAssignment(null);
            return;
        }
        setSelectedAssignment(assignment);
        setBins([]);
        setEdits({});
        setRemarkEdits({});
        setMessages({});
        setBinsLoading(true);

        try {
            if (assignment.status === 'pending') {
                await api.inventory.updateAssignmentStatus(assignment.id, 'in_progress');
                setAssignments(prev => prev.map(a => a.id === assignment.id ? { ...a, status: 'in_progress' } : a));
                setSelectedAssignment(prev => ({ ...prev, status: 'in_progress' }));
            }
            const res = await api.inventory.getCycleCountBins(assignment.sku, assignment.warehouse);
            if (res.data) {
                setBins(res.data);
                const init = {};
                res.data.forEach(b => { init[b.location_name] = b.quantity.toString(); });
                setEdits(init);
                setRemarkEdits(assignment.remarks || {});
                setBinFlags({});

                if (window.innerWidth < 992) {
                    setTimeout(() => {
                        const el = document.getElementById(`details-${assignment.id}`);
                        if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    }, 100);
                }
            }
        } catch (e) {
            console.error('Failed to fetch bins', e);
        } finally {
            setBinsLoading(false);
        }
    };

    const handleQuantityChange = (loc, val) => {
        if (/^\d*$/.test(val)) {
            setEdits(prev => ({ ...prev, [loc]: val }));
            setBinFlags(prev => ({ ...prev, [loc]: null }));
        }
    };

    const handleBinFlag = (loc, flag, dbQty) => {
        setBinFlags(prev => {
            const current = prev[loc];
            const next = current === flag ? null : flag;
            if (next === 'noChange') setEdits(e => ({ ...e, [loc]: dbQty.toString() }));
            else if (next === 'empty') setEdits(e => ({ ...e, [loc]: '0' }));
            return { ...prev, [loc]: next };
        });
    };

    const handleSave = async (location_name) => {
        const newQtyStr = edits[location_name];
        if (!newQtyStr || newQtyStr.trim() === '') {
            setMessages(prev => ({ ...prev, [location_name]: { type: 'error', text: 'Qty required' } }));
            return;
        }
        setUpdating(prev => ({ ...prev, [location_name]: true }));
        try {
            const locRemark = remarkEdits[location_name] || '';
            await api.inventory.updateCycleCountBin({
                sku: selectedAssignment.sku,
                warehouse: selectedAssignment.warehouse,
                location_name,
                quantity: parseInt(newQtyStr, 10),
                assignment_id: selectedAssignment.id,
                remark: locRemark
            });
            setMessages(prev => ({ ...prev, [location_name]: { type: 'success', text: 'Saved!' } }));
            setSelectedAssignment(prev => ({ ...prev, remarks: { ...(prev.remarks || {}), [location_name]: locRemark } }));
            setTimeout(() => setMessages(prev => ({ ...prev, [location_name]: null })), 4000);
        } catch (err) {
            setMessages(prev => ({ ...prev, [location_name]: { type: 'error', text: 'Failed' } }));
        } finally {
            setUpdating(prev => ({ ...prev, [location_name]: false }));
        }
    };

    const handleMarkComplete = async () => {
        if (!selectedAssignment) return;
        setMarkingDone(true);
        try {
            await api.inventory.updateAssignmentStatus(selectedAssignment.id, 'completed');
            setAssignments(prev => prev.map(a => a.id === selectedAssignment.id ? { ...a, status: 'completed' } : a));
            setSelectedAssignment(prev => ({ ...prev, status: 'completed' }));
        } catch (e) {
            alert('Failed.');
        } finally {
            setMarkingDone(false);
        }
    };

    const renderAssignmentDetails = (assignment) => {
        if (!assignment || selectedAssignment?.id !== assignment.id) return null;
        return (
            <div className="card table-container" id={`details-${assignment.id}`} style={{ border: isMobile ? '2px solid var(--primary)' : 'none', margin: isMobile ? '0.5rem 0' : 0 }}>
                <div style={{ padding: '1rem', borderBottom: '1px solid #f1f5f9', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '0.5rem', background: isMobile ? 'rgba(79,70,229,0.03)' : 'transparent' }}>
                    <div>
                        <div style={{ fontWeight: 700, color: 'var(--primary)', fontSize: '1rem' }}>{assignment.sku}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>{assignment.warehouse} • {assignment.client_name}</div>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <StatusBadge status={assignment.status} />
                        {assignment.status !== 'completed' && (
                            <button onClick={handleMarkComplete} disabled={markingDone} style={{ padding: '0.4rem 0.8rem', borderRadius: '6px', border: 'none', background: '#10b981', color: 'white', fontWeight: 600, fontSize: '0.75rem', cursor: 'pointer' }}>
                                Mark Done
                            </button>
                        )}
                    </div>
                </div>
                {binsLoading ? (
                    <div style={{ padding: '2rem', textAlign: 'center' }}><RefreshCcw size={20} className="spin" /></div>
                ) : (
                    <div style={{ overflowX: 'auto' }}>
                        <table className="responsive-table">
                            <thead>
                                <tr>
                                    <th>Bin</th>
                                    <th>DB Qty</th>
                                    <th style={{ textAlign: 'center' }}>No Change</th>
                                    <th style={{ textAlign: 'center' }}>Mark Bin Empty</th>
                                    <th>Actual</th>
                                    <th>Remark</th>
                                    <th>Save</th>
                                </tr>
                            </thead>
                            <tbody>
                                {bins.map(bin => {
                                    const loc = bin.location_name;
                                    const isUpdating = updating[loc];
                                    const msg = messages[loc];
                                    const savedRemark = assignment.remarks?.[loc] || '';
                                    const hasChanged = (edits[loc] !== bin.quantity.toString()) || (remarkEdits[loc] || '' !== savedRemark);
                                    const flag = binFlags[loc] || null;
                                    const isCompleted = assignment.status === 'completed';
                                    return (
                                        <tr key={loc}>
                                            <td data-label="Bin" style={{ fontWeight: 600 }}>{loc}</td>
                                            <td data-label="DB Qty">{bin.quantity}</td>
                                            <td data-label="OK" style={{ textAlign: 'center' }}>
                                                <input type="checkbox" checked={flag === 'noChange'} disabled={isCompleted} onChange={() => handleBinFlag(loc, 'noChange', bin.quantity)} />
                                            </td>
                                            <td data-label="Empty" style={{ textAlign: 'center' }}>
                                                <input type="checkbox" checked={flag === 'empty'} disabled={isCompleted} onChange={() => handleBinFlag(loc, 'empty', bin.quantity)} />
                                            </td>
                                            <td data-label="Actual">
                                                <input type="text" value={edits[loc] || ''} disabled={isCompleted || flag} onChange={e => handleQuantityChange(loc, e.target.value)} style={{ width: '60px', textAlign: 'center' }} />
                                            </td>
                                            <td data-label="Remark">
                                                <input type="text" value={remarkEdits[loc] || ''} disabled={isCompleted} onChange={e => setRemarkEdits(p => ({ ...p, [loc]: e.target.value }))} style={{ width: '100px' }} />
                                            </td>
                                            <td data-label="Action">
                                                {!isCompleted && (
                                                    <button onClick={() => handleSave(loc)} disabled={isUpdating || !hasChanged} className="btn-primary" style={{ padding: '0.3rem 0.6rem', fontSize: '0.7rem' }}>
                                                        {isUpdating ? '...' : 'Save'}
                                                    </button>
                                                )}
                                                {msg && <span style={{ fontSize: '0.7rem', color: msg.type === 'success' ? '#10b981' : '#ef4444' }}>{msg.text}</span>}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>
        );
    };

    const activeAssignments = assignments.filter(a => a.status !== 'completed');
    const doneAssignments = assignments.filter(a => a.status === 'completed');

    return (
        <div style={{ padding: isMobile ? '0.75rem' : '1.5rem', maxWidth: '1400px', margin: '0 auto' }}>
            <h1 style={{ fontSize: '1.5rem', fontWeight: 800, marginBottom: '1rem' }}>My Tasks</h1>
            <div style={{ display: 'flex', gap: '1rem', flexDirection: isMobile ? 'column' : 'row' }}>
                <div style={{ width: isMobile ? '100%' : '320px' }}>
                    <div className="card">
                        <div style={{ padding: '0.75rem', fontWeight: 700, borderBottom: '1px solid #eee' }}>Active ({activeAssignments.length})</div>
                        {activeAssignments.map(a => (
                            <React.Fragment key={a.id}>
                                <AssignmentCard assignment={a} isSelected={selectedAssignment?.id === a.id} onClick={() => handleSelectAssignment(a)} />
                                {isMobile && selectedAssignment?.id === a.id && renderAssignmentDetails(a)}
                            </React.Fragment>
                        ))}
                    </div>
                    {doneAssignments.length > 0 && (
                        <div className="card" style={{ marginTop: '1rem' }}>
                            <div style={{ padding: '0.75rem', fontWeight: 700, borderBottom: '1px solid #eee' }}>Done ({doneAssignments.length})</div>
                            {doneAssignments.map(a => (
                                <React.Fragment key={a.id}>
                                    <AssignmentCard assignment={a} isSelected={selectedAssignment?.id === a.id} onClick={() => handleSelectAssignment(a)} dimmed />
                                    {isMobile && selectedAssignment?.id === a.id && renderAssignmentDetails(a)}
                                </React.Fragment>
                            ))}
                        </div>
                    )}
                </div>
                {!isMobile && (
                    <div style={{ flex: 1 }}>
                        {selectedAssignment ? renderAssignmentDetails(selectedAssignment) : (
                            <div className="card" style={{ padding: '4rem', textAlign: 'center', color: '#999' }}>Select a task to start</div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

const AssignmentCard = ({ assignment, isSelected, onClick, dimmed }) => (
    <div onClick={onClick} style={{ padding: '0.75rem 1rem', borderBottom: '1px solid #eee', cursor: 'pointer', background: isSelected ? '#f5f7ff' : 'transparent', borderLeft: isSelected ? '3px solid #4f46e5' : '3px solid transparent', opacity: dimmed ? 0.6 : 1 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
            <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>{assignment.sku}</span>
            <ChevronRight size={14} style={{ opacity: 0.3 }} />
        </div>
        <div style={{ fontSize: '0.75rem', color: '#666', marginTop: '0.2rem' }}>{assignment.warehouse} • {assignment.client_name}</div>
        <div style={{ marginTop: '0.4rem' }}><StatusBadge status={assignment.status} /></div>
    </div>
);

const CycleCount = () => {
    const { user } = useAuth();
    if (!user) return null;
    return user.role === 'picker' ? <PickerCycleCount /> : <AdminCycleCount />;
};

export default CycleCount;